import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
/**
 * LiveTimer component that displays elapsed or remaining live meeting time.
 *
 * @param startTime - ISO string date to start counting from
 * @param endTime - Optional ISO string date to count down to
 * @param mode - Timer mode to render
 * @param className - Optional CSS class name for styling
 * @returns A timer display showing elapsed time in hh:mm:ss format
 */
export function LiveTimer(_a) {
    var startTime = _a.startTime, endTime = _a.endTime, _b = _a.mode, mode = _b === void 0 ? 'elapsed' : _b, className = _a.className;
    var _c = useState(0), timerValue = _c[0], setTimerValue = _c[1];
    useEffect(function () {
        var startDate = new Date(startTime);
        var endDate = endTime ? new Date(endTime) : null;
        var getTimerValue = function () {
            var now = new Date();
            if (mode === 'countdown' && endDate) {
                return Math.max(0, endDate.getTime() - now.getTime());
            }
            return Math.max(0, now.getTime() - startDate.getTime());
        };
        setTimerValue(getTimerValue());
        // Set up interval to update timer every second
        var interval = setInterval(function () {
            setTimerValue(getTimerValue());
        }, 1000);
        // Cleanup interval on unmount
        return function () { return clearInterval(interval); };
    }, [endTime, mode, startTime]);
    /**
     * Formats milliseconds into hh:mm:ss format
     */
    var formatTime = function (milliseconds) {
        var totalSeconds = Math.floor(milliseconds / 1000);
        var hours = Math.floor(totalSeconds / 3600);
        var minutes = Math.floor((totalSeconds % 3600) / 60);
        var seconds = totalSeconds % 60;
        return "".concat(hours.toString().padStart(2, '0'), ":").concat(minutes
            .toString()
            .padStart(2, '0'), ":").concat(seconds.toString().padStart(2, '0'));
    };
    /**
     * Gets the relative time string using date-fns
     */
    var getRelativeTime = function () {
        if (mode === 'countdown' && endTime) {
            return timerValue === 0
                ? 'Ended'
                : "Ends ".concat(formatDistanceToNow(new Date(endTime), { addSuffix: true }));
        }
        if (timerValue === 0)
            return 'Just started';
        return formatDistanceToNow(new Date(startTime), { addSuffix: true });
    };
    var label = mode === 'countdown' && endTime ? 'Remaining time' : 'Elapsed time';
    var formattedTime = formatTime(timerValue);
    return (<time dateTime={mode === 'countdown' && endTime ? endTime : startTime} className={className} aria-label={"".concat(label, ": ").concat(formattedTime)} title={getRelativeTime()}>
      {formattedTime}
    </time>);
}
